import React from 'react';

// Constellation
import { Anchor, Text } from '@zillow/constellation';

// Components
import ResponsiveHeading from 'app/shared/StyledComponents/ResponsiveText';

// Misc
import * as S from './styles';

const StaticCommonQuestions = () => {
  return (
    <>
      <ResponsiveHeading level="2" fontType="h3" marginBottom="md">
        Common questions about renting your home
      </ResponsiveHeading>
      <Text as="h3" fontType="h4" marginBottom="xs">
        1. How should I price my rental?
      </Text>
      <Text as="div" fontType="body" marginBottom="md">
        To determine your rent price, consider local rent control laws, the rental rates of homes in your area (rental
        comps), the features of your home, and changes in your local market. How much you charge for rent is ultimately
        up to you, but to get a quick starting point, try our free Rent Zestimate<sup>®</sup> tool. To learn more about
        pricing your rental, check out this{' '}
        <Anchor
          href="https://www.zillow.com/rental-manager/resources/how-much-can-i-rent-my-house-for/"
          target="_blank"
        >
          article
        </Anchor>
        .
      </Text>
      <Text as="h3" fontType="h4" marginBottom="xs">
        2. How is my Rent Zestimate<sup>®</sup> calculated?
      </Text>
      <Text as="div" fontType="body" marginBottom="md">
        The Rent Zestimate<sup>®</sup> tool helps provide a rent estimate by address. To come up with the Zillow Rent
        Zestimate
        <sup>®</sup>, we look at publicly available data to determine:
      </Text>
      <Text as="div" fontType="body" marginBottom="md">
        <S.ListContainer>
          <S.BulletItem>
            The home’s physical attributes and amenities (like square footage and number of bedrooms and bathrooms)
          </S.BulletItem>
          <S.BulletItem>Comparable rental properties and the market rental rates in the area</S.BulletItem>
          <S.BulletItem>Any owner-updated home facts, plus other public data like the last sale price</S.BulletItem>
        </S.ListContainer>
      </Text>
      <Text as="div" fontType="body" marginBottom="md">
        Remember that this is just a rent estimate — it’s not set in stone but serves as a resource for landlords and
        property managers. Learn more about the Rent Zestimate<sup>®</sup>{' '}
        <Anchor href="https://www.zillow.com/rental-manager/resources/what-is-the-rent-zestimate/" target="_blank">
          here
        </Anchor>
        .
      </Text>
      <Text as="h3" fontType="h4" marginBottom="xs">
        3. How can I advertise my rental?
      </Text>
      <Text as="div" fontType="body" marginBottom="md">
        Most renters search online when looking for a home, so it’s important to maximize your listing’s exposure across
        multiple brands. With Zillow Rental Manager, you can{' '}
        <Anchor href="https://www.zillow.com/rental-manager/post-a-listing/" target="_blank">
          advertise your rental listings
        </Anchor>{' '}
        across three of the top five rental networks — Zillow, Trulia, and HotPads.* exposing your rental listing to
        over 30 million monthly users.**
      </Text>
      <Text as="h3" fontType="h4" marginBottom="xs">
        4. What should I include in my rental listing?
      </Text>
      <Text as="div" fontType="body" marginBottom="md">
        Some key elements to include in your listing are:
      </Text>
      <Text as="div" fontType="body" marginBottom="md">
        <S.ListContainer>
          <S.BulletItem>
            A detailed rental property description (including square footage, number of bedrooms and bathrooms)
          </S.BulletItem>
          <S.BulletItem>Rent price, lease duration, and required fees</S.BulletItem>
          <S.BulletItem>At least 10 high-quality photos</S.BulletItem>
          <S.BulletItem>
            Elevate your listing with a 3D Home<sup>®</sup> tour - easily include a 3D virtual tour using the free{' '}
            <Anchor href="https://www.zillow.com/marketing/3d-home/" target="_blank">
              Zillow 3D Home<sup>®</sup> app
            </Anchor>
          </S.BulletItem>
          <S.BulletItem>Parking and pet restrictions</S.BulletItem>
          <S.BulletItem>Shared amenities</S.BulletItem>
          <S.BulletItem>Proximity to transit, dining, shopping, etc.</S.BulletItem>
        </S.ListContainer>
      </Text>
      <Text as="div" fontType="body" marginBottom="md">
        Get more information about{' '}
        <Anchor href="https://www.zillow.com/rental-manager/resources/how-to-write-a-rental-ad/" target="_blank">
          writing your listing here.
        </Anchor>
      </Text>
      <Text as="h3" fontType="h4" marginBottom="xs">
        5. When should I raise or lower my rent price?
      </Text>
      <Text as="div" fontType="body" marginBottom="md">
        As a landlord, you may consider increasing your rent if market rates, property taxes, insurance premiums, or
        homeowners association fees have increased. Also, consider if there are property maintenance expenses that need
        to be covered.{' '}
        <Anchor href="https://www.zillow.com/rental-manager/resources/raising-rent/" target="_blank">
          This guide
        </Anchor>{' '}
        can help you make your decision.
      </Text>
      <Text as="div" fontType="body" marginBottom="lg">
        If you’re struggling to find renters, you may consider lowering your rental price. You may also try rethinking
        your terms (i.e. offering short-term leases) or offering a discount on the first month’s rent. Just make sure
        you’re aware of what your budget can accommodate.
      </Text>
      <Text fontType="finePrint" as="p" marginBottom="xs">
        <em>
          *Source: Comscore Media Metrix<sup>®</sup> Multi-Platform Key Measures, Custom-defined list including:
          Zillow.com Rentals, Trulia.com Rentals, HotPads.com, Apartments.com, Rent.com, Apartmentguide.com,
          Apartmenthomeliving.com, Forrent.com, Apartmentfinder.com, Rentals.com, Total Unique Visitors, December 2019
        </em>
      </Text>
      <Text fontType="finePrint" as="p" marginBottom="xs">
        <em>**Source: Zillow Internal Data, 2021</em>
      </Text>
    </>
  );
};

export default StaticCommonQuestions;
